/** New contract — from Contracts or from the client's sheet (D-126): the client, the package, the
 * template of the service (or a free contract typed by hand), the amounts and schedule (prefilled
 * from the package), the dates and the object. The draft then goes through the usual checks,
 * specimen, generation (number, PDF with the layout), sending for signature or paper signature. */

import {
  type LanguageEnum,
  useCatalogPackagesList,
  useContractsItemsCreate,
  useContractsTemplatesList,
  useCrmContactsList,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Button, GlassSheet, SelectField, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { Plus, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { amountOf } from "../billing/shared";
import { errorText, useRefreshContracts } from "./shared";

const LANGUAGES: LanguageEnum[] = ["fr", "en"];
const FREE = "__free__";

interface Row {
  key: number;
  amount: string;
  label: string;
  step: string | null;
}

let nextKey = 1;

export function NewContractSheet({
  open,
  onClose,
  contactId,
  contactName,
  contactLanguage,
}: {
  open: boolean;
  onClose: () => void;
  contactId?: string;
  contactName?: string;
  contactLanguage?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshContracts();
  const [search, setSearch] = useState("");
  const [contact, setContact] = useState(contactId ?? "");
  const [pack, setPack] = useState("");
  const [template, setTemplate] = useState("");
  const [language, setLanguage] = useState<LanguageEnum>(contactLanguage === "en" ? "en" : "fr");
  const [minor, setMinor] = useState(false);
  const [custom, setCustom] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [text, setText] = useState("");
  const [localError, setLocalError] = useState("");
  const contacts = useCrmContactsList({ q: search.trim() || undefined }, { query: { enabled: open && !contactId } });
  const packages = useCatalogPackagesList(undefined, { query: { enabled: open } });
  const templates = useContractsTemplatesList(undefined, { query: { enabled: open } });
  const create = useContractsItemsCreate();
  const errors = fieldErrors(create.error);

  const contactList = contacts.data?.status === 200 ? contacts.data.data.results : [];
  const packageList = (packages.data?.status === 200 ? packages.data.data.results : []).filter((p) => p.status === "published");
  const chosen = packageList.find((p) => p.id === pack);
  const templateList = (templates.data?.status === 200 ? templates.data.data.results : []).filter(
    (tpl) => tpl.status === "published" && tpl.code !== "contrat-libre" && (!chosen || !tpl.service || tpl.service === chosen.service),
  );
  const free = template === FREE;
  const total = rows.reduce((sum, row) => sum + (amountOf(row.amount) || 0), 0);

  const choosePackage = (id: string) => {
    setPack(id);
    setTemplate("");
    const next = packageList.find((p) => p.id === id);
    setRows(
      (next?.installments ?? []).map((i) => ({
        key: nextKey++,
        amount: i.amount_xaf != null ? String(i.amount_xaf) : "",
        label: localized(i.label, language),
        step: i.trigger_step ?? null,
      })),
    );
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!contact || !pack) return;
    const typed = custom || free;
    if (typed && (!rows.length || rows.some((row) => !(amountOf(row.amount) > 0)))) return setLocalError(t("contracts.manual.amountsRequired"));
    if (free && !text.trim()) return setLocalError(t("contracts.manual.objectRequired"));
    if (free && (!start || !end)) return setLocalError(t("contracts.manual.datesRequired"));
    if (start && end && end < start) return setLocalError(t("contracts.manual.endBeforeStart"));
    setLocalError("");
    create.mutate(
      {
        data: {
          contact,
          package: pack,
          language,
          is_minor: minor,
          ...(free ? { free: true } : template ? { template } : {}),
          ...(typed
            ? {
                installments: rows.map((row) => ({
                  amount_xaf: amountOf(row.amount),
                  label: row.label.trim(),
                  ...(row.step !== null ? { trigger_step: row.step } : {}),
                })),
              }
            : {}),
          ...(start ? { start_date: start } : {}),
          ...(end ? { end_date: end } : {}),
          ...(text.trim() ? { custom_text: text.trim() } : {}),
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          refresh();
          onClose();
          navigate(`/contracts/${response.data.id}`);
        },
      },
    );
  };

  const contactOptions = [
    { value: "", label: t("contracts.new.pickContact") },
    ...contactList.map((c) => ({ value: c.id, label: [c.full_name, c.phone_e164].filter(Boolean).join(" · ") })),
  ];
  const packageOptions = [
    { value: "", label: t("contracts.new.pickPackage") },
    ...packageList.map((p) => ({
      value: p.id,
      label: `${p.code} — ${localized(p.name, i18n.language)}${p.total_xaf != null ? ` · ${formatXaf(p.total_xaf)}` : ""}`,
    })),
  ];
  const templateOptions = [
    { value: "", label: t("contracts.manual.defaultTemplate") },
    ...templateList.map((tpl) => ({ value: tpl.id, label: localized(tpl.name, i18n.language) })),
    { value: FREE, label: t("contracts.manual.free") },
  ];

  return (
    <GlassSheet open={open} title={t("contracts.new.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <p className="muted">{t("contracts.new.intro")}</p>
        {contactId ? (
          <p>
            {t("contracts.new.contact")} : <strong>{contactName}</strong>
          </p>
        ) : (
          <>
            <TextField label={t("contracts.new.searchContact")} hint={t("contracts.new.searchHint")} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <SelectField label={t("contracts.new.contact")} value={contact} error={errors.contact} options={contactOptions} onChange={(e) => setContact(e.target.value)} />
          </>
        )}
        <SelectField
          label={t("contracts.new.package")}
          hint={t("contracts.new.packageHint")}
          value={pack}
          error={errors.package}
          options={packageOptions}
          onChange={(e) => choosePackage(e.target.value)}
        />
        {chosen?.requires_second_client ? <p className="muted">{t("contracts.new.coupleNote")}</p> : null}
        {pack ? (
          <SelectField
            label={t("contracts.manual.template")}
            hint={free ? t("contracts.manual.freeHint") : t("contracts.manual.templateHint")}
            value={template}
            error={errors.template}
            options={templateOptions}
            onChange={(e) => setTemplate(e.target.value)}
          />
        ) : null}
        <div className="grid-2">
          <SelectField
            label={t("contracts.fields.language")}
            value={language}
            options={LANGUAGES.map((l) => ({ value: l, label: t(`contracts.languages.${l}`) }))}
            onChange={(e) => setLanguage(e.target.value as LanguageEnum)}
          />
          <SoftToggle label={t("contracts.fields.isMinor")} checked={minor} onChange={setMinor} />
        </div>
        {minor ? <p className="muted">{t("contracts.new.minorNote")}</p> : null}
        {free ? (
          <TextAreaField
            label={t("contracts.manual.object")}
            hint={t("contracts.manual.objectHint")}
            rows={4}
            maxLength={8000}
            value={text}
            error={errors.custom_text}
            onChange={(e) => setText(e.target.value)}
          />
        ) : null}
        <div className="grid-2">
          <TextField label={t("contracts.manual.start")} type="date" value={start} error={errors.start_date} onChange={(e) => setStart(e.target.value)} />
          <TextField label={t("contracts.manual.end")} type="date" value={end} min={start || undefined} error={errors.end_date} onChange={(e) => setEnd(e.target.value)} />
        </div>
        {pack ? (
          <>
            {!free ? <SoftToggle label={t("contracts.manual.customAmounts")} checked={custom} onChange={setCustom} /> : null}
            {custom || free ? (
              <div className="stack">
                <p className="muted">{t("contracts.manual.amountsHint")}</p>
                {rows.map((row, index) => (
                  <div key={row.key} className="invoice-line__numbers">
                    <TextField
                      label={t("contracts.manual.instalment", { n: index + 1 })}
                      inputMode="numeric"
                      value={row.amount ? formatXaf(amountOf(row.amount), { suffix: false }) : ""}
                      onChange={(e) => setRows((all) => all.map((r) => (r.key === row.key ? { ...r, amount: e.target.value.replace(/\D/g, "") } : r)))}
                    />
                    <TextField
                      label={t("contracts.manual.condition")}
                      value={row.label}
                      maxLength={200}
                      onChange={(e) => setRows((all) => all.map((r) => (r.key === row.key ? { ...r, label: e.target.value } : r)))}
                    />
                    <Button variant="ghost" aria-label={t("contracts.manual.remove", { n: index + 1 })} onClick={() => setRows((all) => all.filter((r) => r.key !== row.key))}>
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <div className="row between">
                  <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setRows((all) => [...all, { key: nextKey++, amount: "", label: "", step: null }])}>
                    {t("contracts.manual.addInstalment")}
                  </Button>
                  <span>
                    {t("contracts.list.total")} <strong className="ao-num">{formatXaf(total)}</strong>
                  </span>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {localError ? (
          <p role="alert" className="ao-field__error">
            {localError}
          </p>
        ) : null}
        {create.isError ? (
          <p role="alert" className="ko">
            {errorText(create.error, t("common.error"))}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={!contact || !pack || create.isPending}>
            {create.isPending ? t("common.loading") : t("contracts.new.create")}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}
